import axios from 'axios';

// Force same domain for API calls in production
const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://netloxproxies.com' : 'http://localhost:3101';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types
export interface User {
  id: number;
  name: string;
  email: string;
  proxyUserId: string;
  proxyPassword: string;
  credits: number;
  total_usage_bytes?: number;
  apiKey: string;
  // Referral partner fields (only populated if user is a partner)
  isReferralPartner?: boolean;
  referralCode?: string | null;
  referralBalance?: number;
}

// Referral system types
export interface ReferralTransaction {
  id: number;
  buyerEmail: string;
  purchaseUsd: number;
  commissionUsd: number;
  commissionPct: number;
  bonusGbPct: number;
  gbAmount: number;
  date: string;
}

export interface ReferralStats {
  partner: {
    referralCode: string;
    referralLink: string;
    commissionPercentage: number;
    bonusGbPercentage: number;
    balance: number;
  };
  stats: {
    totalEarned: number;
    totalReferrals: number;
    totalVolumeUsd: number;
  };
  transactions: ReferralTransaction[];
}

export interface Proxy {
  id: number;
  username: string;
  password: string;
  endpoint: string;
  country: string;
  duration: number;
  createdAt: string;
  expiresAt: string;
  remainingMinutes: number;
  isActive: boolean;
  
  // Lazy creation and usage tracking
  status: 'ready' | 'mapped' | 'active';
  ipwebMapped: boolean;
  dataUsedMB: number;
  lastUsedAt?: string;
  
  // Hidden debug info
  ipwebUsername?: string;
  ipwebGateway?: string;
  
  formats: {
    standard: string;           // username:password@host:port (old default)
    hostPort: string;          // host:port:username:password (new default)
    hostPortAt: string;        // host:port@username:password
    userPassHost: string;      // username:password:host:port
    url: string;
    endpoint: string;
    host: string;
    port: string;
    username: string;
    password: string;
  };
}

export interface ProxyStats {
  total: number;
  ready: number;
  mapped: number;
  active: number;
  totalDataUsed: number;
  remainingCredits: number;
}

export interface ProxyListResponse {
  success: boolean;
  proxies: Proxy[];
  stats: ProxyStats;
  endpoint: string;
  user: {
    proxyUserId: string;
    credits: number;
  };
  error?: string;
}

export interface CreateProxyRequest {
  country: string;
  duration: number;
  count: number;
}

// API functions
export const authAPI = {
  // Get current user - TEST: Use HeresMyLinks pattern
  getCurrentUser: async (): Promise<{ success: boolean; user?: User; error?: string }> => {
    try {
      const response = await api.get('/auth/user');
      // This endpoint already returns properly mapped data
      console.log('🔍 Frontend: User data from /auth/user:', response.data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get user info'
      };
    }
  },

  // Logout
  logout: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post('/auth/logout');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Logout failed'
      };
    }
  },

  // Simple Google login - just redirect to backend
  loginWithGoogle: () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  },

  // Local Authentication Methods

  // Register with email and password
  registerWithEmail: async (data: { 
    email: string; 
    password: string; 
    name: string; 
  }): Promise<{ 
    success: boolean; 
    message?: string; 
    error?: string; 
    requiresVerification?: boolean; 
  }> => {
    try {
      const response = await api.post('/auth/register', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  },

  // Login with email and password
  loginWithEmail: async (data: { 
    email: string; 
    password: string; 
  }): Promise<{ 
    success: boolean; 
    message?: string; 
    user?: User; 
    error?: string; 
    requiresVerification?: boolean; 
  }> => {
    try {
      const response = await api.post('/auth/login', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
        requiresVerification: error.response?.data?.requiresVerification || false
      };
    }
  },

  // Verify email address
  verifyEmail: async (token: string): Promise<{ 
    success: boolean; 
    message?: string; 
    error?: string;
    alreadyVerified?: boolean; 
  }> => {
    try {
      const response = await api.get(`/auth/verify-email?token=${token}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Email verification failed'
      };
    }
  },

  // Resend verification email
  resendVerification: async (email: string): Promise<{ 
    success: boolean; 
    message?: string; 
    error?: string; 
  }> => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend verification email'
      };
    }
  },

  // Request password reset
  forgotPassword: async (email: string): Promise<{ 
    success: boolean; 
    message?: string; 
    error?: string; 
  }> => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to process request'
      };
    }
  },

  // Reset password with token
  resetPassword: async (data: { 
    token: string; 
    password: string; 
  }): Promise<{ 
    success: boolean; 
    message?: string; 
    error?: string; 
  }> => {
    try {
      const response = await api.post('/auth/reset-password', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password reset failed'
      };
    }
  },

  // Account API functions
  getAccountBalance: async (): Promise<{ success: boolean; balance?: number; currency?: string; error?: string }> => {
    try {
      const response = await api.get('/auth/account/balance');
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch balance' };
    }
  },

  getApiKey: async (): Promise<{ success: boolean; apiKey?: string; error?: string }> => {
    try {
      const response = await api.get('/auth/account/api-key');
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch API key' };
    }
  },
  
  // Top-up code redemption
  redeemCode: async (code: string): Promise<{ 
    success: boolean; 
    credits: number; 
    message: string; 
    newBalance: number;
  }> => {
    try {
      const response = await api.post('/auth/redeem-code', { code });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        credits: 0,
        message: error.response?.data?.error || 'Failed to redeem code',
        newBalance: 0
      };
    }
  },

  // Process batch proxies
  processBatch: async (batchId: string): Promise<{ 
    success: boolean; 
    message: string; 
    details?: any; 
  }> => {
    try {
      const response = await api.post('/auth/process-batch', { batchId });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to process batch'
      };
    }
  },

  // Create crypto payment with NowPayments
  // Optional referralCode: if valid, buyer gets bonus GB and partner earns commission
  createCryptoPayment: async (paymentData: { 
    gbAmount: number; 
    priceUsd: number;
    referralCode?: string; // optional referral code entered at checkout
  }): Promise<{ 
    success: boolean; 
    invoiceUrl?: string;
    invoiceId?: string;
    gbAmount?: number;  // final GB amount (includes bonus if code applied)
    bonusGb?: number;   // how many bonus GB were added
    message?: string;
    error?: string;
  }> => {
    try {
      const response = await api.post('/auth/create-crypto-payment', paymentData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || 'Failed to create payment',
        error: error.response?.data?.error || 'Failed to create payment'
      };
    }
  },

  // Admin login
  adminLogin: async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const response = await api.post('/auth/admin/login', { username, password });
    return response.data;
  },

  // Generate top-up codes
  generateCodes: async (data: { creditsGB: number; quantity: number; expiresAt: string | null }): Promise<{ codes: any[] }> => {
    const response = await api.post('/admin/codes/create', data);
    return response.data;
  },

  // Get all users (admin)
  getAllUsers: async (): Promise<{ success: boolean; users: any[]; totalUsers: number }> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  // Get global usage statistics (admin)
  getGlobalUsageStats: async (): Promise<{ success: boolean; stats: any[] }> => {
    const response = await api.get('/admin/usage/global');
    return response.data;
  },

  // Get mobile proxy hourly statistics (admin)
  getMobileProxyStats: async (hours: number = 24): Promise<{ 
    success: boolean; 
    hourlyStats: any[]; 
    proxyTotals: any[];
    peakHours: any[];
    hoursQueried: number;
  }> => {
    const response = await api.get(`/admin/mobile-proxy-stats?hours=${hours}`);
    return response.data;
  }
};

export const proxyAPI = {
  // Create proxies
  createProxies: async (data: CreateProxyRequest): Promise<{ success: boolean; proxies?: Proxy[]; error?: string }> => {
    try {
      const response = await api.post('/api/proxies/create', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create proxies'
      };
    }
  },

  // List user's proxies
  listProxies: async (): Promise<ProxyListResponse> => {
    try {
      const response = await api.get('/api/proxies/list');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        proxies: [],
        stats: {
          total: 0,
          ready: 0,
          mapped: 0,
          active: 0,
          totalDataUsed: 0,
          remainingCredits: 0
        },
        endpoint: '',
        user: {
          proxyUserId: '',
          credits: 0
        },
        error: error.response?.data?.error || 'Failed to fetch proxies'
      };
    }
  }
};

// Location API
export const locationAPI = {
  /**
   * Get all available countries
   */
  getCountries: async () => {
    const response = await api.get('/api/locations/countries');
    return response.data;
  },

  /**
   * Get states/regions for a specific country
   */
  getStates: async (countryCode: string) => {
    const response = await api.get(`/api/locations/countries/${countryCode}/states`);
    return response.data;
  },

  /**
   * Get cities for a specific state in a country
   */
  getCities: async (countryCode: string, stateCode: string) => {
    const response = await api.get(`/api/locations/countries/${countryCode}/states/${stateCode}/cities`);
    return response.data;
  },

  /**
   * Get display name for a location string
   */
  getDisplayName: async (locationString: string) => {
    const response = await api.get(`/api/locations/display-name/${encodeURIComponent(locationString)}`);
    return response.data;
  },

  /**
   * Get location statistics
   */
  getStats: async () => {
    const response = await api.get('/api/locations/stats');
    return response.data;
  },

  /**
   * Test location mapping (for debugging)
   */
  testMapping: async (locationString: string) => {
    const response = await api.post('/api/locations/test-mapping', { locationString });
    return response.data;
  },

  /**
   * Get global usage statistics (Admin only)
   */
  getGlobalUsageStats: async () => {
    const response = await api.get('/admin/usage/global');
    return response.data;
  }
};

// Referral API — only used by referral partners and checkout flow
export const referralAPI = {
  /**
   * Get partner dashboard stats (commission balance, history, etc.)
   * Requires user to be a referral partner.
   */
  getStats: async (): Promise<{ success: boolean; data?: ReferralStats; error?: string }> => {
    try {
      const response = await api.get('/api/referral/stats');
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load referral stats'
      };
    }
  },

  /**
   * Validate a referral code entered at checkout.
   * Returns bonus GB percentage if valid.
   */
  validateCode: async (code: string): Promise<{
    success: boolean;
    valid?: boolean;
    bonusGbPercentage?: number;
    message?: string;
    error?: string;
  }> => {
    try {
      const response = await api.get(`/api/referral/validate/${encodeURIComponent(code.toUpperCase())}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        valid: false,
        error: error.response?.data?.error || 'Failed to validate code'
      };
    }
  }
};
 